import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Typography, 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Progress,
  Table,
  Tag,
  Space,
  Button,
  Alert,
  Spin
} from 'antd';
import { 
  RadarChartOutlined,
  EyeOutlined,
  ThunderboltOutlined,
  SafetyOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  AlertOutlined,
  BarChartOutlined,
  SettingOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  Timeline
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { aiService } from '../../services/ai';

const { Content } = Layout;
const { Title, Text } = Typography;

interface ThreatDetection {
  id: string;
  timestamp: string;
  source: string;
  threat_level: 'low' | 'medium' | 'high' | 'critical';
  threat_score: number;
  description: string;
  location?: string;
  status: 'active' | 'resolved' | 'investigating';
}

interface RadarStats {
  totalThreats: number;
  activeThreats: number;
  resolvedThreats: number;
  criticalThreats: number;
  averageResponseTime: string;
  detectionAccuracy: number;
}

const RadarAIDashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [aiStatus, setAiStatus] = useState<'online' | 'offline' | 'maintenance'>('online');
  const [stats, setStats] = useState<RadarStats>({
    totalThreats: 0,
    activeThreats: 0,
    resolvedThreats: 0,
    criticalThreats: 0,
    averageResponseTime: '0ms',
    detectionAccuracy: 0
  });
  const [recentDetections, setRecentDetections] = useState<ThreatDetection[]>([]);
  
  // Tab Management
  const [activeTab, setActiveTab] = useState('threats');

  // Mock data for demonstration
  useEffect(() => {
    loadRadarData();
    
    // Simulate real-time updates
    const interval = setInterval(() => {
      updateRealTimeData();
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const loadRadarData = async () => {
    setLoading(true);
    try {
      // Check AI service status
      const healthStatus = await aiService.getHealth();
      setAiStatus(healthStatus.status === 'healthy' ? 'online' : 'offline');
      
      // Mock radar stats
      setStats({
        totalThreats: 1247,
        activeThreats: 23,
        resolvedThreats: 1224,
        criticalThreats: 3,
        averageResponseTime: '1.2s',
        detectionAccuracy: 94.7
      });

      // Mock recent detections
      setRecentDetections([
        {
          id: '1',
          timestamp: new Date().toISOString(),
          source: 'Emergency Alert System',
          threat_level: 'high',
          threat_score: 0.87,
          description: 'Suspicious activity pattern detected in Kadıköy region',
          location: 'İstanbul, Kadıköy',
          status: 'investigating'
        },
        {
          id: '2',
          timestamp: new Date(Date.now() - 300000).toISOString(),
          source: 'User Behavior Analysis',
          threat_level: 'medium',
          threat_score: 0.65,
          description: 'Anomalous login pattern detected',
          status: 'active'
        },
        {
          id: '3',
          timestamp: new Date(Date.now() - 600000).toISOString(),
          source: 'Network Monitoring',
          threat_level: 'critical',
          threat_score: 0.95,
          description: 'Multiple failed authentication attempts',
          location: 'İstanbul, Beşiktaş',
          status: 'resolved'
        }
      ]);
    } catch (error) {
      console.error('Failed to load radar data:', error);
      setAiStatus('offline');
    } finally {
      setLoading(false);
    }
  };

  const updateRealTimeData = () => {
    // Simulate real-time threat detection
    if (Math.random() > 0.7) {
      const newThreat: ThreatDetection = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        source: 'Real-time Monitor',
        threat_level: Math.random() > 0.8 ? 'high' : 'medium',
        threat_score: Math.random() * 0.4 + 0.5,
        description: 'New threat pattern detected',
        status: 'active'
      };
      
      setRecentDetections(prev => [newThreat, ...prev.slice(0, 9)]);
      setStats(prev => ({
        ...prev,
        totalThreats: prev.totalThreats + 1,
        activeThreats: prev.activeThreats + 1
      }));
    }
  };

  const getThreatLevelColor = (level: string) => {
    switch (level) {
      case 'critical': return '#ff4d4f';
      case 'high': return '#ff7a45';
      case 'medium': return '#ffa940';
      case 'low': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'error';
      case 'investigating': return 'warning';
      case 'resolved': return 'success';
      default: return 'default';
    }
  };

  const detectionColumns: ColumnsType<ThreatDetection> = [
    {
      title: 'Zaman',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp: string) => (
        <Text type="secondary">
          {new Date(timestamp).toLocaleString('tr-TR')}
        </Text>
      ),
      width: 150,
    },
    {
      title: 'Kaynak',
      dataIndex: 'source',
      key: 'source',
      width: 180,
    },
    {
      title: 'Tehdit Seviyesi',
      dataIndex: 'threat_level',
      key: 'threat_level',
      render: (level: string, record: ThreatDetection) => (
        <Space>
          <div 
            style={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              backgroundColor: getThreatLevelColor(level) 
            }} 
          />
          <Text style={{ color: getThreatLevelColor(level), fontWeight: 'bold' }}>
            {level.toUpperCase()}
          </Text>
          <Text type="secondary">({(record.threat_score * 100).toFixed(1)}%)</Text>
        </Space>
      ),
      width: 150,
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Konum',
      dataIndex: 'location',
      key: 'location',
      render: (location: string) => location || '-',
      width: 120,
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status === 'active' ? 'Aktif' : 
           status === 'investigating' ? 'İnceleniyor' : 'Çözüldü'}
        </Tag>
      ),
      width: 100,
    },
  ];

  return (
    <Content style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Space align="center" style={{ marginBottom: 16 }}>
          <RadarChartOutlined style={{ fontSize: 32, color: '#1890ff' }} />
          <Title level={2} style={{ margin: 0 }}>RADAR AI Dashboard</Title>
          <Tag color={aiStatus === 'online' ? 'green' : 'red'}>
            {aiStatus === 'online' ? '🟢 Online' : '🔴 Offline'}
          </Tag>
        </Space>
        
        <Text type="secondary">
          Real-time AI-powered threat detection and behavioral analysis system
        </Text>
      </div>

      {/* AI Status Alert */}
      {aiStatus === 'offline' && (
        <Alert
          message="AI Service Offline"
          description="RADAR AI system is currently offline. Some features may not be available."
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
          action={
            <Button size="small" onClick={loadRadarData}>
              <ReloadOutlined /> Retry Connection
            </Button>
          }
        />
      )}

      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Toplam Tehdit Tespiti"
              value={stats.totalThreats}
              prefix={<EyeOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Aktif Tehditler"
              value={stats.activeThreats}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Kritik Tehditler"
              value={stats.criticalThreats}
              prefix={<ThunderboltOutlined />}
              valueStyle={{ color: '#ff7a45' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Çözülen Tehditler"
              value={stats.resolvedThreats}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* RADAR AI Tabs - ENHANCED VISIBILITY */}
      <Card style={{ 
        marginBottom: '24px', 
        border: '2px solid #722ed1',
        zIndex: 9999,
        position: 'relative'
      }}>
        <div style={{ 
          marginBottom: '16px', 
          padding: '16px',
          backgroundColor: '#f0f2ff',
          borderRadius: '8px',
          zIndex: 10000,
          position: 'relative'
        }}>
          <Title level={4} style={{ marginBottom: '16px', color: '#722ed1' }}>
            📊 RADAR AI Sistemi - Analiz Modülleri
          </Title>
          <Space size="large" wrap>
            <Button 
              type={activeTab === 'threats' ? 'primary' : 'default'}
              icon={<AlertOutlined />}
              onClick={() => setActiveTab('threats')}
              size="large"
              style={{ 
                height: '48px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              🚨 Tehdit Tespiti
            </Button>
            <Button 
              type={activeTab === 'patterns' ? 'primary' : 'default'}
              icon={<BarChartOutlined />}
              onClick={() => setActiveTab('patterns')}
              size="large"
              style={{ 
                height: '48px',
                fontSize: '14px',
                fontWeight: 'bold',
                backgroundColor: activeTab === 'patterns' ? '#52c41a' : undefined
              }}
            >
              📊 Pattern Analizi
            </Button>
            <Button 
              type={activeTab === 'behavior' ? 'primary' : 'default'}
              icon={<EyeOutlined />}
              onClick={() => setActiveTab('behavior')}
              size="large"
              style={{ 
                height: '48px',
                fontSize: '14px',
                fontWeight: 'bold',
                backgroundColor: activeTab === 'behavior' ? '#1890ff' : undefined
              }}
            >
              🧠 Davranış Anomalisi
            </Button>
            <Button 
              type={activeTab === 'models' ? 'primary' : 'default'}
              icon={<SettingOutlined />}
              onClick={() => setActiveTab('models')}
              size="large"
              style={{ 
                height: '48px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              ⚙️ AI Model Yönetimi
            </Button>
          </Space>
          
          {/* Active Tab Indicator */}
          <div style={{ marginTop: '16px', padding: '8px', backgroundColor: '#fff', borderRadius: '4px' }}>
            <Text strong style={{ color: '#722ed1' }}>
              Aktif Modül: {
                activeTab === 'threats' ? '🚨 Tehdit Tespiti' :
                activeTab === 'patterns' ? '📊 Pattern Analizi' :
                activeTab === 'behavior' ? '🧠 Davranış Anomalisi' :
                '⚙️ AI Model Yönetimi'
              }
            </Text>
          </div>
        </div>
      </Card>

      {/* Tab Content */}
      {activeTab === 'threats' && (
        <>
          {/* Performance Metrics */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card title="🎯 Tespit Doğruluğu">
            <Progress
              type="circle"
              percent={stats.detectionAccuracy}
              format={percent => `${percent}%`}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Text type="secondary">AI Model Accuracy</Text>
            </div>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="⚡ Sistem Performansı">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text>Ortalama Yanıt Süresi</Text>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                  {stats.averageResponseTime}
                </div>
              </div>
              <div>
                <Text>Sistem Durumu</Text>
                <div>
                  <Tag color="green" icon={<SafetyOutlined />}>
                    Optimal Performance
                  </Tag>
                </div>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Recent Detections */}
      <Card 
        title="🚨 Son Tehdit Tespitleri" 
        extra={
          <Button 
            icon={<ReloadOutlined />} 
            onClick={loadRadarData}
            loading={loading}
          >
            Yenile
          </Button>
        }
      >
        <Table
          columns={detectionColumns}
          dataSource={recentDetections}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Card>
        </>
      )}

      {/* Pattern Analysis Tab */}
      {activeTab === 'patterns' && (
        <div>
          <Title level={4} style={{ color: '#722ed1', marginBottom: 24 }}>
            📊 AI Pattern Analizi Dashboard
          </Title>
          
          {/* Pattern Analizi İstatistikleri */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="Tespit Edilen Pattern"
                  value={847}
                  prefix={<BarChartOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="Aktif Tehdit Paterni"
                  value={12}
                  prefix={<AlertOutlined />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="Prediction Doğruluğu"
                  value={91.4}
                  precision={1}
                  prefix={<CheckCircleOutlined />}
                  suffix="%"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="Model Güven Skoru"
                  value={8.7}
                  precision={1}
                  prefix={<SafetyOutlined />}
                  suffix="/10"
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Tehdit Pattern Tablosu */}
          <Card 
            title="🎯 Aktif Tehdit Patternleri"
            extra={
              <Space>
                <Button icon={<ReloadOutlined />} size="small">
                  Yenile
                </Button>
                <Button type="primary" size="small">
                  Yeni Pattern Ekle
                </Button>
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            <Table
              size="small"
              dataSource={[
                {
                  key: '1',
                  pattern: 'Gece Saatleri Yoğun Aktivite',
                  category: 'Zaman Bazlı',
                  riskLevel: 'Yüksek',
                  frequency: '23 kez',
                  lastSeen: '1 saat önce',
                  confidence: 94,
                  action: 'Otomatik Uyarı'
                },
                {
                  key: '2',
                  pattern: 'Çoklu Lokasyon Erişimi',
                  category: 'Konum Bazlı',
                  riskLevel: 'Kritik',
                  frequency: '8 kez',
                  lastSeen: '15 dakika önce',
                  confidence: 97,
                  action: 'Acil Müdahale'
                }
              ]}
              columns={[
                {
                  title: 'Pattern Adı',
                  dataIndex: 'pattern',
                  key: 'pattern'
                },
                {
                  title: 'Kategori',
                  dataIndex: 'category',
                  key: 'category',
                  width: 120,
                  render: (category: string) => (
                    <Tag color="blue">{category}</Tag>
                  )
                },
                {
                  title: 'Risk',
                  dataIndex: 'riskLevel',
                  key: 'riskLevel',
                  width: 80,
                  render: (level: string) => (
                    <Tag color={
                      level === 'Kritik' ? 'red' :
                      level === 'Yüksek' ? 'orange' :
                      level === 'Orta' ? 'gold' : 'green'
                    }>
                      {level}
                    </Tag>
                  )
                },
                {
                  title: 'Güven',
                  dataIndex: 'confidence',
                  key: 'confidence',
                  width: 70,
                  render: (confidence: number) => `%${confidence}`
                }
              ]}
              pagination={{ pageSize: 5 }}
            />
          </Card>
        </div>
      )}

      {/* Behavior Analysis Tab */}
      {activeTab === 'behavior' && (
        <div>
          <Title level={4} style={{ color: '#722ed1', marginBottom: 24 }}>
            🧠 Davranış Anomalisi Tespiti
          </Title>
          
          {/* Davranış Analizi İstatistikleri */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="Analiz Edilen Kullanıcı"
                  value={1247}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="Tespit Edilen Anomali"
                  value={23}
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="Risk Skoru Ortalaması"
                  value={7.3}
                  precision={1}
                  prefix={<SafetyOutlined />}
                  suffix="/10"
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="Doğruluk Oranı"
                  value={94.7}
                  precision={1}
                  prefix={<CheckCircleOutlined />}
                  suffix="%"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Son Davranış Anomalileri */}
          <Card 
            title="🚨 Son Tespit Edilen Davranış Anomalileri"
            extra={
              <Button icon={<ReloadOutlined />} size="small">
                Yenile
              </Button>
            }
          >
            <Table
              size="small"
              dataSource={[
                {
                  key: '1',
                  user: 'Kullanıcı-4521',
                  anomaly: 'Olağandışı giriş saatleri',
                  riskLevel: 'Orta',
                  location: 'Kadıköy, İstanbul',
                  timestamp: '2 dakika önce',
                  confidence: 87
                },
                {
                  key: '2', 
                  user: 'Kullanıcı-7832',
                  anomaly: 'Hızlı konum değişimi',
                  riskLevel: 'Yüksek',
                  location: 'Beşiktaş → Sarıyer',
                  timestamp: '8 dakika önce',
                  confidence: 92
                }
              ]}
              columns={[
                {
                  title: 'Kullanıcı',
                  dataIndex: 'user',
                  key: 'user',
                  width: 120
                },
                {
                  title: 'Anomali Türü',
                  dataIndex: 'anomaly',
                  key: 'anomaly'
                },
                {
                  title: 'Risk Seviyesi',
                  dataIndex: 'riskLevel',
                  key: 'riskLevel',
                  width: 100,
                  render: (level: string) => (
                    <Tag color={
                      level === 'Kritik' ? 'red' :
                      level === 'Yüksek' ? 'orange' :
                      level === 'Orta' ? 'gold' : 'green'
                    }>
                      {level}
                    </Tag>
                  )
                },
                {
                  title: 'Güven',
                  dataIndex: 'confidence',
                  key: 'confidence',
                  width: 80,
                  render: (confidence: number) => `%${confidence}`
                }
              ]}
              pagination={{ pageSize: 5 }}
            />
          </Card>
        </div>
      )}

      {/* AI Model Management Tab */}
      {activeTab === 'models' && (
        <div>
          <Title level={4} style={{ color: '#722ed1', marginBottom: 24 }}>
            ⚙️ AI Model Yönetimi
          </Title>
          <Card>
            <Text>AI Model yönetim paneli yakında eklenecek...</Text>
          </Card>
        </div>
      )}

    </Content>
  );
};

export default RadarAIDashboard;
